import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { OrderShort } from '~/api/Order';
import { useEstimatedTime } from '~/components/ActiveOrderCard/hooks';
import {
  MobileHeaderCard,
  type MobileHeaderCardProps,
} from '~/components/MobileHeaderCard';
import { MobileOrderCard } from '~/components/MobileOrderCard';

import { mainStore, orderStore } from '~/stores';

import {
  HEADER_ORDER_STATUSES,
  MAX_ORDERS_COUNT,
  OBSERVER_KEY,
} from './constants';
import styles from './HeaderOrders.module.scss';
import SeeAllButton from './SeeAllButton';
import {
  getBackgroundForStatus,
  getIconForStatus,
  getLabelDeclension,
} from './utils';

const HeaderOrders = ({
  className,
  onTransitionEnd,
  overlayComponent,
  overlayClassName,
  dropdownClassName,
  showLabel = true,
}: Pick<
  MobileHeaderCardProps,
  | 'onTransitionEnd'
  | 'className'
  | 'overlayComponent'
  | 'overlayClassName'
  | 'dropdownClassName'
> & { showLabel?: boolean }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [activeOrders, setActiveOrders] = useState<OrderShort[]>([]);

  useEffect(() => {
    setActiveOrders(
      orderStore.orderList
        ?.filter((item) => HEADER_ORDER_STATUSES.includes(item.public_status))
        ?.sort(sortFn) ?? [],
    );
  }, [orderStore.orderList]);

  useEffect(() => {
    orderStore.setOrdersPollingEnabled(true);
  }, []);

  const isMultipleOrders = activeOrders.length > 1;
  const firstOrder = activeOrders.at(0);

  const firstOrderEstimation = useEstimatedTime(activeOrders.at(0), {
    todayAtLabel: 'phrases:willArrive',
  });

  const iconType = isMultipleOrders
    ? 'puzzle-piece'
    : getIconForStatus(firstOrder?.public_status);

  const background = isMultipleOrders
    ? 'select'
    : getBackgroundForStatus(firstOrder?.public_status);

  const title = (() => {
    if (isMultipleOrders) {
      const ordersCount = activeOrders.length;
      return `${ordersCount} ${t(
        getLabelDeclension(ordersCount, ['order', 'orders']),
      )}`;
    }

    const publicStatus = `${firstOrder?.public_status}`;

    return t(publicStatus === 'picking' ? 'packing' : publicStatus);
  })();

  const handleClickOrder = (order: OrderShort) => () => {
    navigate(`/order/${order.id}`);
    mainStore.setHeaderDropdownOpen(OBSERVER_KEY, false);
  };

  if (!activeOrders.length) {
    return null;
  }

  return (
    <MobileHeaderCard
      observerKey={OBSERVER_KEY}
      caption={showLabel ? firstOrderEstimation : undefined}
      title={showLabel ? title : undefined}
      icon={iconType}
      backgroundVariant={background}
      isLoading={orderStore.isLoading}
      onTransitionEnd={onTransitionEnd}
      className={className}
      dropdownClassName={dropdownClassName}
      overlayClassName={overlayClassName}
      overlayComponent={overlayComponent}
      onClick={
        !isMultipleOrders && firstOrder
          ? handleClickOrder(firstOrder)
          : undefined
      }
    >
      {isMultipleOrders && (
        <>
          <div className={styles.orderList}>
            {activeOrders.slice(0, MAX_ORDERS_COUNT).map((order) => (
              <MobileOrderCard
                key={order.id}
                order={order}
                onClick={handleClickOrder(order)}
              />
            ))}
          </div>
          {activeOrders.length > MAX_ORDERS_COUNT && (
            <SeeAllButton
              observerKey={OBSERVER_KEY}
              link="/cabinet/orders"
              label="seeAllOrders"
            />
          )}
        </>
      )}
    </MobileHeaderCard>
  );
};

const sortFn = (a: OrderShort, b: OrderShort) => {
  const aValue = new Date(a.sla.deliver_by_min).getDate();
  const bValue = new Date(b.sla.deliver_by_min).getDate();

  return aValue - bValue;
};

export default observer(HeaderOrders);
